import { MegaMenuDisclosure, NavigationDrawer } from "theme";

const TOFFEE_MEGA_MENU_ELEMENT = "toffee-mega-menu-disclosure";
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 250;
const DESKTOP_NAV_MIN_WIDTH = 1280;
const toffeeHeaderCollisionObservers = new WeakMap();

function setupToffeeHeaderCollisionGuard(header) {
  if (
    !(header instanceof HTMLElement) ||
    toffeeHeaderCollisionObservers.has(header)
  ) {
    return;
  }

  const section = header.closest(".shopify-section--header");
  const mainNavigation = header.querySelector(".header__main-nav");
  const linkList = mainNavigation?.querySelector(".header__link-list");

  if (!(section instanceof HTMLElement) || !(linkList instanceof HTMLElement)) {
    return;
  }

  let animationFrame = null;
  let usesDrawer = null;

  const setOverflowState = (isOverflowing) => {
    section.toggleAttribute("data-toffee-navigation-overflow", isOverflowing);
    const drawer = document.getElementById("header-sidebar-menu");
    drawer?.toggleAttribute("data-toffee-navigation-overflow", isOverflowing);
    const trigger = header.querySelector('[aria-controls="header-sidebar-menu"]');
    const nextUsesDrawer = trigger && getComputedStyle(trigger).display !== "none";
    if (nextUsesDrawer !== usesDrawer) {
      if (nextUsesDrawer) {
        const focusedMenu = document.activeElement?.closest('details[is="toffee-mega-menu-disclosure"]');
        header.querySelectorAll('details[is="toffee-mega-menu-disclosure"]').forEach((menu) => {
          menu._toffeeClearTimers();
          menu.open = false;
        });
        if (focusedMenu && header.contains(focusedMenu)) trigger.focus({ preventScroll: true });
      } else if (drawer?.open) {
        const hadDrawerFocus = drawer.contains(document.activeElement);
        Promise.resolve(drawer.hide()).then(() => {
          if (hadDrawerFocus) header.querySelector(".header__logo a, a.header__logo")?.focus({ preventScroll: true });
        });
      }
      usesDrawer = nextUsesDrawer;
    }
  };

  const measure = () => {
    animationFrame = null;
    header.querySelectorAll(".toffee-mega-menu[data-toffee-menu-v2]").forEach((menu) => {
      menu.style.setProperty("--tm-menu-top", Math.max(0, header.getBoundingClientRect().bottom) + "px");
    });

    if (!header.isConnected || window.innerWidth < DESKTOP_NAV_MIN_WIDTH) {
      setOverflowState(false);
      return;
    }

    const previousStyle = linkList.getAttribute("style");
    linkList.style.setProperty("display", "flex", "important");
    linkList.style.setProperty("position", "absolute");
    linkList.style.setProperty("visibility", "hidden");
    linkList.style.setProperty("width", "max-content");
    linkList.style.setProperty("max-width", "none");

    const requiredWidth = Math.ceil(linkList.getBoundingClientRect().width);
    const availableWidth = Math.floor(mainNavigation.getBoundingClientRect().width);

    if (previousStyle === null) {
      linkList.removeAttribute("style");
    } else {
      linkList.setAttribute("style", previousStyle);
    }

    setOverflowState(requiredWidth > availableWidth);
  };

  const scheduleMeasure = () => {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = window.requestAnimationFrame(measure);
  };

  const resizeObserver = new ResizeObserver(scheduleMeasure);
  resizeObserver.observe(mainNavigation);
  window.addEventListener("resize", scheduleMeasure, { passive: true });
  document.fonts?.ready.then(scheduleMeasure);
  scheduleMeasure();

  toffeeHeaderCollisionObservers.set(header, {
    disconnect() {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      setOverflowState(false);
    },
  });
}

function initializeToffeeHeaderCollisionGuards(root = document) {
  root
    .querySelectorAll?.(".header.toffee-navigation-enabled")
    .forEach(setupToffeeHeaderCollisionGuard);
}

class ToffeeMegaMenuDisclosure extends MegaMenuDisclosure {
  constructor() {
    super();

    this._toffeeOpenTimer = null;
    this._toffeeCloseTimer = null;
    this._toffeeSuppressFocusOpen = false;
    this._toffeeTransitionId = 0;
    this._toffeeDocumentClickListener = this._toffeeHandleDocumentClick.bind(this);
    this._toffeeFocusInListener = this._toffeeHandleFocusIn.bind(this);

    this.addEventListener("focusin", this._toffeeFocusInListener);
  }

  connectedCallback() {
    if (typeof super.connectedCallback === "function") {
      super.connectedCallback();
    }

    this.summaryElement.setAttribute("aria-expanded", String(this.open));
    document.addEventListener("click", this._toffeeDocumentClickListener);
  }

  disconnectedCallback() {
    this._toffeeClearTimers();
    this._toffeeTransitionId += 1;

    document.removeEventListener("click", this._toffeeDocumentClickListener);
    this._toffeeDetachInheritedDocumentListeners();

    this._open = false;
    this.removeAttribute("open");
    this.summaryElement.setAttribute("aria-expanded", "false");

    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
  }

  async _transition(value) {
    if (value) {
      const header = this.closest("store-header");
      this.contentElement.style.setProperty("--tm-menu-top", Math.max(0, header.getBoundingClientRect().bottom) + "px");
    }
    const transitionId = ++this._toffeeTransitionId;
    this.summaryElement.setAttribute("aria-expanded", String(value));

    await super._transition(value);

    if (!this.isConnected) {
      this._toffeeDetachInheritedDocumentListeners();
      return;
    }

    if (value && transitionId !== this._toffeeTransitionId && !this.open) {
      this._toffeeDetachInheritedDocumentListeners();
    }

    if (!value && transitionId !== this._toffeeTransitionId && this.open) {
      this.setAttribute("open", "");
      this._toffeeAttachInheritedDocumentListeners();
    }
  }

  _transitionIn(hasOpenSiblings) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.themeVariables?.settings?.reduceMenuAnimation) {
      this.contentElement.style.opacity = "1";
      return Promise.resolve();
    }
    return super._transitionIn(hasOpenSiblings);
  }

  _transitionOut() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.themeVariables?.settings?.reduceMenuAnimation) {
      this.contentElement.style.opacity = "0";
      return Promise.resolve();
    }
    return super._transitionOut();
  }

  _detectHover(event) {
    if (this.trigger !== "hover") {
      return;
    }

    const relatedTarget = event.relatedTarget;

    if (relatedTarget instanceof Node && this.contains(relatedTarget)) {
      return;
    }

    if (event.type === "mouseover") {
      window.clearTimeout(this._toffeeCloseTimer);
      window.clearTimeout(this._toffeeOpenTimer);

      if (!this.open) {
        this._toffeeOpenTimer = window.setTimeout(() => {
          this._toffeeOpenTimer = null;
          this.open = true;
        }, OPEN_DELAY_MS);
      }
    } else if (event.type === "mouseout") {
      window.clearTimeout(this._toffeeOpenTimer);
      window.clearTimeout(this._toffeeCloseTimer);

      if (this.open) {
        this._toffeeCloseTimer = window.setTimeout(() => {
          this._toffeeCloseTimer = null;

          if (!this.matches(":focus-within")) {
            this.open = false;
          }
        }, CLOSE_DELAY_MS);
      }
    }
  }

  _detectEscKeyboard(event) {
    if (event.key !== "Escape" && event.code !== "Escape") {
      return;
    }

    const targetMenu =
      event.target instanceof Element
        ? event.target.closest(`details[is="${TOFFEE_MEGA_MENU_ELEMENT}"]`)
        : null;

    if (targetMenu && targetMenu !== this) {
      return;
    }

    if (!this.open && !this.hasAttribute("open")) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this._toffeeClearTimers();
    this._toffeeSuppressFocusOpen = true;
    this.open = false;
    this.summaryElement.focus({ preventScroll: true });

    queueMicrotask(() => {
      this._toffeeSuppressFocusOpen = false;
    });
  }

  _toffeeHandleDocumentClick(event) {
    if (!(event.target instanceof Node) || this.contains(event.target)) {
      return;
    }

    this._toffeeClearTimers();

    if (this.open || this.hasAttribute("open")) {
      this.open = false;
    }
  }

  _toffeeHandleFocusIn() {
    if (this.trigger !== "hover" || this._toffeeSuppressFocusOpen) {
      return;
    }

    this._toffeeClearTimers();
    this.open = true;
  }

  _toffeeAttachInheritedDocumentListeners() {
    document.addEventListener("click", this._detectClickOutsideListener);
    document.addEventListener("keydown", this._detectEscKeyboardListener);
    document.addEventListener("focusout", this._detectFocusOutListener);
  }

  _toffeeDetachInheritedDocumentListeners() {
    document.removeEventListener("click", this._detectClickOutsideListener);
    document.removeEventListener("keydown", this._detectEscKeyboardListener);
    document.removeEventListener("focusout", this._detectFocusOutListener);
  }

  _toffeeClearTimers() {
    window.clearTimeout(this._toffeeOpenTimer);
    window.clearTimeout(this._toffeeCloseTimer);
    this._toffeeOpenTimer = null;
    this._toffeeCloseTimer = null;
  }
}

class ToffeeNavigationDrawer extends NavigationDrawer {
  constructor() {
    super();
    this._toffeeActivePanelIndex = 0;
    this._toffeeReturnFocusByPanel = new Map();
    this._toffeePanelAnimation = null;
    this._toffeeFocusFrame = null;
  }

  get initialFocus() {
    return this.querySelector('button[is="close-button"]') || this;
  }

  get _toffeeReduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.themeVariables?.settings?.reduceDrawerAnimation;
  }

  connectedCallback() {
    super.connectedCallback();
    const panels = Array.from(this.querySelectorAll(".panel"));
    this.querySelectorAll("button[data-panel]").forEach((button) => {
      const [panelIndex, wrapperIndex] = button.dataset.panel.split("-").map(Number);
      const target = Number.isInteger(wrapperIndex)
        ? panels[panelIndex]?.querySelectorAll(".panel__wrapper")[wrapperIndex]
        : panels[panelIndex];
      if (target) {
        target.id ||= `${this.id}-panel-${panelIndex}${Number.isInteger(wrapperIndex) ? "-" + wrapperIndex : ""}`;
        button.setAttribute("aria-controls", target.id);
      }
    });
    this.reinitializeDrawer();
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this._toffeeFocusFrame);
    this._toffeePanelAnimation?.cancel();
    super.disconnectedCallback();
  }

  _showTransition(animate = true) {
    if (!this._toffeeReduceMotion) return super._showTransition(animate);
    this._setInitialPosition();
    this.style.opacity = "1";
    this.style.visibility = "visible";
    this.classList.add("show-close-cursor");
    return Promise.resolve();
  }

  _hideTransition() {
    if (!this._toffeeReduceMotion) return super._hideTransition();
    this.style.opacity = "0";
    this.style.visibility = "hidden";
    this.classList.remove("show-close-cursor");
    return Promise.resolve();
  }

  _onPanelButtonClick(event, target) {
    const [panelIndex, wrapperIndex] = target.dataset.panel.split("-").map(Number);
    if (panelIndex > this._toffeeActivePanelIndex) {
      this._toffeeReturnFocusByPanel.set(this._toffeeActivePanelIndex, target);
    }
    this.switchToPanel(panelIndex, Number.isInteger(wrapperIndex) ? wrapperIndex : null);
  }

  switchToPanel(panelIndex, wrapperIndex = null) {
    const panels = Array.from(this.querySelectorAll(".panel"));
    const index = Number(panelIndex);
    const targetPanel = panels[index];
    if (!targetPanel) return;
    const isBack = index < this._toffeeActivePanelIndex;
    this._toffeePanelAnimation?.cancel();
    window.cancelAnimationFrame(this._toffeeFocusFrame);
    panels.forEach((panel, currentIndex) => {
      const active = currentIndex === index;
      panel.style.opacity = active ? "1" : "0";
      // Active panels must inherit the closed drawer's hidden visibility.
      panel.style.visibility = active ? "inherit" : "hidden";
      panel.style.transform = "none";
      panel.toggleAttribute("inert", !active);
      panel.toggleAttribute("aria-hidden", !active);
      if (!active) panel.setAttribute("aria-hidden", "true");
      panel.querySelectorAll(".panel__wrapper").forEach((wrapper, currentWrapper) => {
        const selected = active && (wrapperIndex === null ? currentWrapper === 0 : currentWrapper === Number(wrapperIndex));
        wrapper.hidden = !selected;
        wrapper.toggleAttribute("inert", !selected);
        if (selected && !isBack) wrapper.scrollTop = 0;
      });
    });
    this.querySelectorAll('button[data-panel^="1-"]').forEach((button) => {
      button.setAttribute("aria-expanded", String(index === 1 && button.dataset.panel === `1-${wrapperIndex}`));
    });
    this._toffeeActivePanelIndex = index;
    const focusTarget = isBack ? this._toffeeReturnFocusByPanel.get(index) : targetPanel.querySelector('.panel__wrapper:not([hidden]) .tm-back button');
    this._toffeeFocusFrame = window.requestAnimationFrame(() => {
      if (this.open) (focusTarget || this.initialFocus)?.focus({ preventScroll: true });
    });
    if (!this._toffeeReduceMotion) {
      const direction = (isBack ? -1 : 1) * (document.dir === "rtl" ? -1 : 1);
      this._toffeePanelAnimation = targetPanel.animate(
        [{ opacity: 0, transform: `translateX(${direction * 16}px)` }, { opacity: 1, transform: "translateX(0)" }],
        { duration: 180, easing: "ease-out" }
      );
    }
  }

  reinitializeDrawer() {
    this._toffeePanelAnimation?.cancel();
    window.cancelAnimationFrame(this._toffeeFocusFrame);
    this._toffeeActivePanelIndex = 0;
    this._toffeeReturnFocusByPanel.clear();
    this.querySelectorAll(".panel").forEach((panel, index) => {
      panel.style.opacity = index === 0 ? "1" : "0";
      panel.style.visibility = index === 0 ? "inherit" : "hidden";
      panel.style.transform = "none";
      panel.toggleAttribute("inert", index !== 0);
      if (index !== 0) panel.setAttribute("aria-hidden", "true");
      else panel.removeAttribute("aria-hidden");
      panel.querySelectorAll(".panel__wrapper").forEach((wrapper) => {
        wrapper.hidden = index !== 0;
        wrapper.toggleAttribute("inert", index !== 0);
        wrapper.scrollTop = 0;
      });
    });
    this.querySelectorAll('button[data-panel^="1-"]').forEach((button) => button.setAttribute("aria-expanded", "false"));
  }
}

if (!window.customElements.get(TOFFEE_MEGA_MENU_ELEMENT)) {
  window.customElements.define(
    TOFFEE_MEGA_MENU_ELEMENT,
    ToffeeMegaMenuDisclosure,
    { extends: "details" }
  );
}

if (!window.customElements.get("toffee-navigation-drawer")) {
  window.customElements.define(
    "toffee-navigation-drawer",
    ToffeeNavigationDrawer
  );
}

initializeToffeeHeaderCollisionGuards();

document.addEventListener("shopify:section:load", (event) => {
  initializeToffeeHeaderCollisionGuards(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  event.target
    .querySelectorAll?.(".header.toffee-navigation-enabled")
    .forEach((header) => {
      toffeeHeaderCollisionObservers.get(header)?.disconnect();
      toffeeHeaderCollisionObservers.delete(header);
    });
});
